/** @module @liquidefy/code-editor/themes/glass/surfaces @version 1.0.0 @license LQD Research LLC */
/** @rust glass_surfaces.rs — false; 4/2/26; sm */

// ═══ §1 TYPES ═══

/**
 * A single glass surface definition. Every visual panel in the editor is
 * rendered through a GlassSurface. The surface is mode-agnostic — dark ↔ light
 * mode is achieved solely by changing the ground layer beneath.
 */
export interface GlassSurface {
  readonly blur: number;              // backdrop-filter blur in px (4-24 typical)
  readonly opacity: number;           // background opacity (0-1, typically 0.02-0.15)
  readonly tintColor: string;         // hex color used as background tint
  readonly tintOpacity: number;       // tint layer opacity (0-1)
  readonly borderOpacity: number;     // border alpha (0-1, typically 0.05-0.15)
  readonly borderColor: string;       // hex for border (usually white)
  readonly shadowSpread: number;      // box-shadow spread in px (0 = no shadow)
  readonly shadowOpacity: number;     // shadow alpha (0-0.3)
  readonly innerGlow: boolean;        // subtle 1px inset top highlight
  readonly innerGlowOpacity: number;  // inner glow alpha
}

/** Named surface slots covering every visual region of the code editor. */
export type SurfaceName =
  | 'editor'
  | 'sidebar'
  | 'tab-bar'
  | 'panel'
  | 'status-bar'
  | 'title-bar'
  | 'input'
  | 'dropdown'
  | 'modal'
  | 'tooltip'
  | 'notification'
  | 'breadcrumb'
  | 'minimap'
  | 'peek'
  | 'widget';

/** A complete set of glass surfaces for every named region. */
export interface GlassSurfaceSet {
  readonly surfaces: Readonly<Record<SurfaceName, GlassSurface>>;
}

/** Resolved CSS values ready to apply to a DOM element. */
export interface GlassCSSOutput {
  readonly background: string;      // rgba(...)
  readonly backdropFilter: string;  // blur(Xpx)
  readonly border: string;          // 1px solid rgba(...)
  readonly boxShadow: string;       // shadow string or 'none'
}

/** Diff between two glass surfaces — which fields changed and by how much. */
export interface SurfaceDiff {
  readonly blurDelta: number;
  readonly opacityDelta: number;
  readonly tintOpacityDelta: number;
  readonly borderOpacityDelta: number;
  readonly shadowSpreadDelta: number;
  readonly shadowOpacityDelta: number;
  readonly innerGlowOpacityDelta: number;
  readonly tintColorChanged: boolean;
  readonly borderColorChanged: boolean;
  readonly innerGlowChanged: boolean;
}

// ═══ §2 CONSTANTS ═══

const ALL_SURFACE_NAMES: readonly SurfaceName[] = [
  'editor', 'sidebar', 'tab-bar', 'panel', 'status-bar', 'title-bar',
  'input', 'dropdown', 'modal', 'tooltip', 'notification', 'breadcrumb',
  'minimap', 'peek', 'widget',
] as const;

const DEFAULT_TINT = '#8090a0';
const DEFAULT_BORDER = '#ffffff';

// ═══ §3 HELPERS ═══

function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const clean = hex.startsWith('#') ? hex.slice(1) : hex;
  const full = clean.length === 3
    ? clean[0]! + clean[0]! + clean[1]! + clean[1]! + clean[2]! + clean[2]!
    : clean;
  const n = parseInt(full, 16);
  return { r: (n >> 16) & 0xff, g: (n >> 8) & 0xff, b: n & 0xff };
}

function rgba(hex: string, alpha: number): string {
  const { r, g, b } = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${clamp01(alpha)})`;
}

function clamp01(v: number): number {
  return Math.max(0, Math.min(1, v));
}

function clampPositive(v: number): number {
  return Math.max(0, v);
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * clamp01(t);
}

// ═══ §4 SURFACE FACTORY ═══

function surface(
  blur: number,
  opacity: number,
  tintColor: string,
  tintOpacity: number,
  borderOpacity: number,
  borderColor: string,
  shadowSpread: number,
  shadowOpacity: number,
  innerGlow: boolean,
  innerGlowOpacity: number,
): GlassSurface {
  return {
    blur: clampPositive(blur),
    opacity: clamp01(opacity),
    tintColor,
    tintOpacity: clamp01(tintOpacity),
    borderOpacity: clamp01(borderOpacity),
    borderColor,
    shadowSpread: clampPositive(shadowSpread),
    shadowOpacity: clamp01(shadowOpacity),
    innerGlow,
    innerGlowOpacity: clamp01(innerGlowOpacity),
  };
}

/** Create a GlassSurface from a partial spec, filling defaults. */
export function createSurface(spec: Partial<GlassSurface> = {}): GlassSurface {
  return surface(
    spec.blur ?? 8,
    spec.opacity ?? 0.04,
    spec.tintColor ?? DEFAULT_TINT,
    spec.tintOpacity ?? 0.02,
    spec.borderOpacity ?? 0.06,
    spec.borderColor ?? DEFAULT_BORDER,
    spec.shadowSpread ?? 0,
    spec.shadowOpacity ?? 0,
    spec.innerGlow ?? false,
    spec.innerGlowOpacity ?? 0,
  );
}

// ═══ §5 DEFAULT SURFACES ═══

/**
 * Create the default glass surface set with carefully tuned values.
 * Editor = barely-there glass so code reads clearly.
 * Chrome = medium frost for visual separation.
 * Overlays = heavy frost with shadows.
 * All tints use neutral blue-gray (#8090a0) — mode-agnostic.
 */
export function createDefaultSurfaces(): GlassSurfaceSet {
  const t = DEFAULT_TINT;
  const b = DEFAULT_BORDER;

  const surfaces: Record<SurfaceName, GlassSurface> = {
    // Depth 0 — content areas (shallowest)
    'editor':       surface(8,  0.03, t, 0.02, 0.05, b, 0, 0,    false, 0),
    'minimap':      surface(6,  0.02, t, 0.01, 0.04, b, 0, 0,    false, 0),
    'breadcrumb':   surface(8,  0.03, t, 0.02, 0.05, b, 0, 0,    false, 0),
    // Depth 1 — chrome panels
    'sidebar':      surface(16, 0.06, t, 0.04, 0.08, b, 0, 0,    true,  0.03),
    'tab-bar':      surface(12, 0.05, t, 0.03, 0.07, b, 0, 0,    true,  0.02),
    'title-bar':    surface(12, 0.05, t, 0.03, 0.07, b, 0, 0,    true,  0.02),
    'status-bar':   surface(12, 0.05, t, 0.03, 0.06, b, 0, 0,    false, 0),
    // Depth 2 — secondary panels and inputs
    'panel':        surface(16, 0.06, t, 0.04, 0.08, b, 0, 0,    true,  0.03),
    'input':        surface(10, 0.04, t, 0.03, 0.10, b, 0, 0,    true,  0.04),
    // Depth 3 — floating overlays
    'dropdown':     surface(20, 0.08, t, 0.05, 0.10, b, 4, 0.12, true,  0.04),
    'peek':         surface(18, 0.07, t, 0.04, 0.09, b, 4, 0.10, true,  0.03),
    'widget':       surface(18, 0.07, t, 0.04, 0.09, b, 4, 0.10, true,  0.03),
    // Depth 4 — modals
    'modal':        surface(24, 0.10, t, 0.06, 0.12, b, 8, 0.15, true,  0.05),
    'notification': surface(20, 0.08, t, 0.05, 0.10, b, 6, 0.12, true,  0.04),
    // Depth 5 — tooltips
    'tooltip':      surface(20, 0.09, t, 0.05, 0.10, b, 4, 0.10, false, 0),
  };

  return { surfaces };
}

// ═══ §6 SURFACE ACCESSORS ═══

/** Retrieve a single named surface from the set. */
export function getSurface(set: GlassSurfaceSet, name: SurfaceName): GlassSurface {
  return set.surfaces[name];
}

/** Replace a single named surface, returning a new immutable set. */
export function setSurface(
  set: GlassSurfaceSet,
  name: SurfaceName,
  s: GlassSurface,
): GlassSurfaceSet {
  return { surfaces: { ...set.surfaces, [name]: s } };
}

/** Partially update a single named surface, merging patch into existing values. */
export function updateSurface(
  set: GlassSurfaceSet,
  name: SurfaceName,
  patch: Partial<GlassSurface>,
): GlassSurfaceSet {
  const current = set.surfaces[name];
  const merged = surface(
    patch.blur ?? current.blur,
    patch.opacity ?? current.opacity,
    patch.tintColor ?? current.tintColor,
    patch.tintOpacity ?? current.tintOpacity,
    patch.borderOpacity ?? current.borderOpacity,
    patch.borderColor ?? current.borderColor,
    patch.shadowSpread ?? current.shadowSpread,
    patch.shadowOpacity ?? current.shadowOpacity,
    patch.innerGlow ?? current.innerGlow,
    patch.innerGlowOpacity ?? current.innerGlowOpacity,
  );
  return setSurface(set, name, merged);
}

/** Get all surface names as an array. */
export function surfaceNames(): readonly SurfaceName[] {
  return ALL_SURFACE_NAMES;
}

/** Check if a string is a valid SurfaceName. */
export function isSurfaceName(name: string): name is SurfaceName {
  return (ALL_SURFACE_NAMES as readonly string[]).includes(name);
}

// ═══ §7 CSS GENERATION ═══

/**
 * Convert a GlassSurface into concrete CSS values.
 * Pass the ground layer's glassTint as `tintHex` so the same surface
 * definition works in both dark and light mode.
 */
export function surfaceToCSS(s: GlassSurface, tintHex: string): GlassCSSOutput {
  const bg = rgba(tintHex, s.opacity);
  const backdropFilter = `blur(${s.blur}px)`;
  const border = `1px solid ${rgba(s.borderColor, s.borderOpacity)}`;

  const shadowParts: string[] = [];

  if (s.shadowSpread > 0 && s.shadowOpacity > 0) {
    const yOffset = Math.round(s.shadowSpread / 2);
    shadowParts.push(`0 ${yOffset}px ${s.shadowSpread}px ${rgba('#000000', s.shadowOpacity)}`);
  }
  if (s.innerGlow && s.innerGlowOpacity > 0) {
    shadowParts.push(`inset 0 1px 0 0 ${rgba('#ffffff', s.innerGlowOpacity)}`);
  }

  const boxShadow = shadowParts.length > 0 ? shadowParts.join(', ') : 'none';
  return { background: bg, backdropFilter, border, boxShadow };
}

/** Generate CSS for every surface using each surface's own tintColor. */
export function allSurfacesToCSS(set: GlassSurfaceSet): Record<SurfaceName, GlassCSSOutput> {
  const result = {} as Record<SurfaceName, GlassCSSOutput>;
  for (const name of ALL_SURFACE_NAMES) {
    const s = set.surfaces[name];
    result[name] = surfaceToCSS(s, s.tintColor);
  }
  return result;
}

/** Generate CSS for every surface using a shared tint (mode-aware). */
export function allSurfacesToCSSWithTint(
  set: GlassSurfaceSet,
  tintHex: string,
): Record<SurfaceName, GlassCSSOutput> {
  const result = {} as Record<SurfaceName, GlassCSSOutput>;
  for (const name of ALL_SURFACE_NAMES) {
    result[name] = surfaceToCSS(set.surfaces[name], tintHex);
  }
  return result;
}

// ═══ §8 CSS CUSTOM PROPERTIES ═══

/**
 * Generate CSS custom properties for a single named surface.
 * Produces --surface-{name}-blur, --surface-{name}-bg, etc.
 */
export function surfaceToCSSVariables(
  name: SurfaceName,
  s: GlassSurface,
): Record<string, string> {
  const prefix = `--surface-${name}`;
  const css = surfaceToCSS(s, s.tintColor);

  return {
    [`${prefix}-blur`]:              `${s.blur}px`,
    [`${prefix}-bg`]:                css.background,
    [`${prefix}-backdrop`]:          css.backdropFilter,
    [`${prefix}-border`]:            css.border,
    [`${prefix}-shadow`]:            css.boxShadow,
    [`${prefix}-opacity`]:           String(s.opacity),
    [`${prefix}-tint-color`]:        s.tintColor,
    [`${prefix}-tint-opacity`]:      String(s.tintOpacity),
    [`${prefix}-border-color`]:      s.borderColor,
    [`${prefix}-border-opacity`]:    String(s.borderOpacity),
    [`${prefix}-shadow-spread`]:     `${s.shadowSpread}px`,
    [`${prefix}-shadow-opacity`]:    String(s.shadowOpacity),
    [`${prefix}-inner-glow`]:        s.innerGlow ? '1' : '0',
    [`${prefix}-inner-glow-opacity`]: String(s.innerGlowOpacity),
  };
}

/** Generate CSS custom properties for all surfaces in the set. */
export function allSurfacesToCSSVariables(set: GlassSurfaceSet): Record<string, string> {
  let result: Record<string, string> = {};
  for (const name of ALL_SURFACE_NAMES) {
    const vars = surfaceToCSSVariables(name, set.surfaces[name]);
    result = { ...result, ...vars };
  }
  return result;
}

// ═══ §9 SCALING ═══

/**
 * Uniformly scale blur and opacity across all surfaces.
 * blurScale multiplies blur and shadowSpread.
 * opacityScale multiplies opacity, tintOpacity, borderOpacity, shadowOpacity, innerGlowOpacity.
 */
export function scaleSurfaceSet(
  set: GlassSurfaceSet,
  blurScale: number,
  opacityScale: number,
): GlassSurfaceSet {
  const bs = clampPositive(blurScale);
  const os = clampPositive(opacityScale);
  const scaled = {} as Record<SurfaceName, GlassSurface>;

  for (const name of ALL_SURFACE_NAMES) {
    const s = set.surfaces[name];
    scaled[name] = surface(
      s.blur * bs, s.opacity * os, s.tintColor, s.tintOpacity * os,
      s.borderOpacity * os, s.borderColor, s.shadowSpread * bs, s.shadowOpacity * os,
      s.innerGlow, s.innerGlowOpacity * os,
    );
  }
  return { surfaces: scaled };
}

/** Override the tint color across all surfaces. */
export function retintSurfaceSet(set: GlassSurfaceSet, tintColor: string): GlassSurfaceSet {
  const retinted = {} as Record<SurfaceName, GlassSurface>;
  for (const name of ALL_SURFACE_NAMES) {
    retinted[name] = { ...set.surfaces[name], tintColor };
  }
  return { surfaces: retinted };
}

/** Override the border color across all surfaces. */
export function reborderSurfaceSet(set: GlassSurfaceSet, borderColor: string): GlassSurfaceSet {
  const result = {} as Record<SurfaceName, GlassSurface>;
  for (const name of ALL_SURFACE_NAMES) {
    result[name] = { ...set.surfaces[name], borderColor };
  }
  return { surfaces: result };
}

// ═══ §10 PRESETS ═══

/** Subtle preset: minimal glass — half blur, half opacity. */
export function createSubtleSurfaces(): GlassSurfaceSet {
  return scaleSurfaceSet(createDefaultSurfaces(), 0.5, 0.5);
}

/** Frosted preset: heavy frost — 1.5x blur, 1.8x opacity. */
export function createFrostedSurfaces(): GlassSurfaceSet {
  return scaleSurfaceSet(createDefaultSurfaces(), 1.5, 1.8);
}

// ═══ §11 INTERPOLATION ═══

/**
 * Linearly interpolate between two glass surfaces.
 * t=0 → a, t=1 → b. Discrete fields snap at t=0.5.
 */
export function interpolateSurfaces(a: GlassSurface, b: GlassSurface, t: number): GlassSurface {
  const ct = clamp01(t);
  const useB = ct >= 0.5;
  return surface(
    lerp(a.blur, b.blur, ct), lerp(a.opacity, b.opacity, ct),
    useB ? b.tintColor : a.tintColor, lerp(a.tintOpacity, b.tintOpacity, ct),
    lerp(a.borderOpacity, b.borderOpacity, ct), useB ? b.borderColor : a.borderColor,
    lerp(a.shadowSpread, b.shadowSpread, ct), lerp(a.shadowOpacity, b.shadowOpacity, ct),
    useB ? b.innerGlow : a.innerGlow, lerp(a.innerGlowOpacity, b.innerGlowOpacity, ct),
  );
}

/** Interpolate every surface in two sets, producing a blended set. */
export function interpolateSurfaceSets(
  a: GlassSurfaceSet,
  b: GlassSurfaceSet,
  t: number,
): GlassSurfaceSet {
  const blended = {} as Record<SurfaceName, GlassSurface>;
  for (const name of ALL_SURFACE_NAMES) {
    blended[name] = interpolateSurfaces(a.surfaces[name], b.surfaces[name], t);
  }
  return { surfaces: blended };
}

// ═══ §12 DIFF AND COMPARISON ═══

/** Compute the difference between two glass surfaces. */
export function diffSurfaces(a: GlassSurface, b: GlassSurface): SurfaceDiff {
  return {
    blurDelta: b.blur - a.blur,
    opacityDelta: b.opacity - a.opacity,
    tintOpacityDelta: b.tintOpacity - a.tintOpacity,
    borderOpacityDelta: b.borderOpacity - a.borderOpacity,
    shadowSpreadDelta: b.shadowSpread - a.shadowSpread,
    shadowOpacityDelta: b.shadowOpacity - a.shadowOpacity,
    innerGlowOpacityDelta: b.innerGlowOpacity - a.innerGlowOpacity,
    tintColorChanged: a.tintColor !== b.tintColor,
    borderColorChanged: a.borderColor !== b.borderColor,
    innerGlowChanged: a.innerGlow !== b.innerGlow,
  };
}

/** Check whether two glass surfaces are structurally equal. */
export function surfacesEqual(a: GlassSurface, b: GlassSurface): boolean {
  return (
    a.blur === b.blur &&
    a.opacity === b.opacity &&
    a.tintColor === b.tintColor &&
    a.tintOpacity === b.tintOpacity &&
    a.borderOpacity === b.borderOpacity &&
    a.borderColor === b.borderColor &&
    a.shadowSpread === b.shadowSpread &&
    a.shadowOpacity === b.shadowOpacity &&
    a.innerGlow === b.innerGlow &&
    a.innerGlowOpacity === b.innerGlowOpacity
  );
}
